'use client';

import clsx from 'clsx';
import { Children, isValidElement, useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import Carousel from 'react-multi-carousel';
import type { StateCallBack } from 'react-multi-carousel/lib/types';

import 'react-multi-carousel/lib/styles.css';

import {
  DASHBOARD_WIDGET_CAROUSEL_DEFAULT_ITEMS_PER_PAGE,
  DASHBOARD_WIDGET_CAROUSEL_NARROW_WIDTH_PX,
  DRAG_CLICK_SUPPRESS_PX,
} from '@/modules/players/v3/constants/constants';
import { useCarouselContainerResize } from '@/modules/players/v3/hooks/use-carousel-container-resize';
import { useIsMobileCard } from '@/modules/players/v3/hooks/use-is-mobile-card';
import type { DashboardWidgetCardCarouselPageLayout } from '@/modules/players/v3/types/dashboard';

export type { DashboardWidgetCardCarouselPageLayout };

export type DashboardWidgetCardCarouselProps = {
  children: ReactNode;
  itemsPerPage?: number;
  mobileItemsPerPage?: number;
  pageItemLayout?: DashboardWidgetCardCarouselPageLayout;
  peekNextPage?: boolean;
  gridCols?: 2 | 3;
  /** When true, disables the multi-column grid layout on mobile and falls back to pageItemLayout. */
  disableMobileGrid?: boolean;
  /**
   * Column layout only: cards keep one height across pages and across widgets in a row. A short page
   * is padded with empty slots, and a single page keeps the space of the dots it doesn't render.
   */
  keepUniformCardHeight?: boolean;
};

function chunkPages<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

type CarouselItemEntry = { element: ReactElement; slotKey: string };

export const DashboardWidgetCardCarousel = ({
  children,
  itemsPerPage: itemsPerPageProp,
  mobileItemsPerPage: mobileItemsPerPageProp,
  pageItemLayout = 'row',
  peekNextPage: peekNextPageProp,
  gridCols,
  disableMobileGrid,
  keepUniformCardHeight,
}: DashboardWidgetCardCarouselProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<InstanceType<typeof Carousel> | null>(null);
  const containerWidth = useCarouselContainerResize(containerRef, carouselRef);

  const isNarrowViewport = useIsMobileCard();
  // A mobile viewport is always narrow — some widgets swap to a mobile-only tree there and
  // expect the mobile counts. A widget sitting in a half-width row is narrow too, whatever the
  // viewport says, which the viewport alone cannot see.
  const isMobile =
    isNarrowViewport ||
    (containerWidth !== null && containerWidth < DASHBOARD_WIDGET_CAROUSEL_NARROW_WIDTH_PX);

  const itemsPerPage = (() => {
    const mobile =
      isMobile && typeof mobileItemsPerPageProp === 'number' && mobileItemsPerPageProp > 0
        ? mobileItemsPerPageProp
        : null;
    const base =
      typeof itemsPerPageProp === 'number' && itemsPerPageProp > 0
        ? itemsPerPageProp
        : DASHBOARD_WIDGET_CAROUSEL_DEFAULT_ITEMS_PER_PAGE;
    return mobile ?? base;
  })();

  const effectiveGridCols = isMobile && disableMobileGrid ? undefined : gridCols;

  const itemEntries = useMemo<CarouselItemEntry[]>(() => {
    const arr = Children.toArray(children).filter(isValidElement);
    return arr.map((child, idx) => ({
      element: child,
      slotKey: child.key != null ? String(child.key) : `carousel-child-${idx}`,
    }));
  }, [children]);

  const pages = useMemo(() => chunkPages(itemEntries, itemsPerPage), [itemEntries, itemsPerPage]);
  const pageCount = pages.length;

  // Keys are built here rather than at render time so the ghost slots keep their identity
  // between renders; regenerating them defeats reconciliation.
  const ghostKeysByPage = useMemo(() => {
    return pages.map((page) => {
      const padsShortPage = pageItemLayout === 'row' || keepUniformCardHeight;
      if (!effectiveGridCols && padsShortPage && page.length < itemsPerPage) {
        return Array.from({ length: itemsPerPage - page.length }, () => crypto.randomUUID());
      }
      return [];
    });
  }, [pages, effectiveGridCols, pageItemLayout, keepUniformCardHeight, itemsPerPage]);

  const showPeek = peekNextPageProp === true;

  const dragGuard = useRef({
    tracking: false,
    startX: 0,
    horizontalDrag: false,
    suppressNextClick: false,
  });
  const [activeSlide, setActiveSlide] = useState(0);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const g = dragGuard.current;
    g.tracking = true;
    g.horizontalDrag = false;
    g.startX = e.clientX;
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const g = dragGuard.current;
    if (!g.tracking) return;
    if (Math.abs(e.clientX - g.startX) > DRAG_CLICK_SUPPRESS_PX) {
      g.horizontalDrag = true;
    }
  };

  const handlePointerEnd = () => {
    const g = dragGuard.current;
    if (!g.tracking) return;
    g.tracking = false;
    if (g.horizontalDrag) g.suppressNextClick = true;
    g.horizontalDrag = false;
  };

  const handleClickCapture = (e: React.MouseEvent) => {
    const g = dragGuard.current;
    if (!g.suppressNextClick) return;
    e.preventDefault();
    e.stopPropagation();
    g.suppressNextClick = false;
  };

  const responsive = useMemo(
    () => ({
      default: {
        breakpoint: { max: 10_000, min: 0 },
        items: 1,
        slidesToSlide: 1,
        ...(showPeek ? { partialVisibilityGutter: 48 } : {}),
      },
    }),
    [showPeek]
  );

  const handleAfterChange = (_previousSlide: number, state: StateCallBack) => {
    setActiveSlide(state.currentSlide);
  };

  const itemWrapperClass = effectiveGridCols
    ? 'min-h-0'
    : pageItemLayout === 'row'
      ? 'min-h-0 min-w-0 flex-1 touch-pan-x'
      : keepUniformCardHeight
        ? // No min-h-0: a card never shrinks under its content; the row grows instead.
          'w-full flex-1'
        : 'min-h-0 w-full flex-1';

  const carouselKey = `${itemsPerPage}-${pageItemLayout}-${effectiveGridCols ?? ''}-${itemEntries.length}-${showPeek}-${isMobile}`;

  const active = pageCount > 1;

  return (
    <div
      ref={containerRef}
      role='region'
      aria-roledescription='carousel'
      aria-label='Content carousel'
      className='flex min-h-0 min-w-0 flex-1 flex-col'
    >
      <div
        className='flex min-h-0 min-w-0 flex-1 flex-col'
        onPointerDown={active ? handlePointerDown : undefined}
        onPointerMove={active ? handlePointerMove : undefined}
        onPointerUp={active ? handlePointerEnd : undefined}
        onPointerCancel={active ? handlePointerEnd : undefined}
        onClickCapture={active ? handleClickCapture : undefined}
      >
        <Carousel
          key={carouselKey}
          ref={carouselRef}
          responsive={responsive}
          infinite={false}
          showDots={false}
          arrows={false}
          swipeable
          draggable
          partialVisible={showPeek}
          minimumTouchDrag={24}
          removeArrowOnDeviceType={['desktop', 'tablet', 'mobile']}
          afterChange={handleAfterChange}
          additionalTransfrom={0}
          centerMode={false}
          className={clsx('min-h-0 flex-1', active && 'cursor-grab')}
          containerClass='flex min-h-0 min-w-0 flex-1 !pl-0 [&_.react-multi-carousel-list]:min-h-0 [&_.react-multi-carousel-list]:!h-full [&_.react-multi-carousel-list]:!items-stretch [&_.react-multi-carousel-track]:min-h-0 [&_.react-multi-carousel-track]:!h-full [&_.react-multi-carousel-item]:!flex [&_.react-multi-carousel-item]:min-h-0 [&_.react-multi-carousel-item]:!h-full'
          sliderClass='!min-h-0 min-h-0 !h-full'
          itemClass='!flex min-h-0 !h-full'
        >
          {pages.map((pageItems, pageIndex) => (
            <div
              key={pageItems.map((e) => e.slotKey).join('|')}
              className={clsx(
                // Whole-card tap targets opt back in like links do; the drag guard still wins mid-swipe.
                '[&_*]:pointer-events-none [&_a]:pointer-events-auto [&_button]:pointer-events-auto [&_[role=link]]:pointer-events-auto [&_[role=button]]:pointer-events-auto [&_[data-flippable]]:pointer-events-auto',
                'min-h-0 w-full gap-2 px-4',
                {
                  'grid grid-cols-2': effectiveGridCols === 2,
                  'grid grid-cols-3': effectiveGridCols === 3,
                  'flex flex-row': !effectiveGridCols && pageItemLayout === 'row',
                  'flex flex-col': !effectiveGridCols && pageItemLayout !== 'row',
                  // 1fr rows size every card to the tallest one; flex-1 would split the summed height and short the taller card.
                  'md:grid md:grid-cols-1 md:auto-rows-fr':
                    !effectiveGridCols && pageItemLayout !== 'row' && keepUniformCardHeight,
                }
              )}
              onDragStartCapture={(e) => e.preventDefault()}
            >
              {pageItems.map(({ element, slotKey }) => (
                <div key={slotKey} className={itemWrapperClass}>
                  {element}
                </div>
              ))}
              {(ghostKeysByPage[pageIndex] ?? []).map((id) => (
                <div key={id} className={itemWrapperClass} aria-hidden />
              ))}
            </div>
          ))}
        </Carousel>
      </div>

      {active ? (
        <div className='mt-4 flex items-center justify-center gap-3'>
          {pages.map((_, i) => (
            <button
              key={i}
              type='button'
              aria-label={`Go to page ${i + 1}`}
              aria-current={i === activeSlide ? 'true' : undefined}
              onClick={() => carouselRef.current?.goToSlide(i)}
              className={clsx(
                'm-0 size-2 shrink-0 rounded-full border-0 p-0 shadow-none outline-none transition-colors',
                i === activeSlide ? 'bg-purple-700' : 'bg-gray-200 hover:bg-gray-300'
              )}
            />
          ))}
        </div>
      ) : keepUniformCardHeight ? (
        <div className='mt-4 h-2' aria-hidden />
      ) : null}
    </div>
  );
};
